/**
 * PostHog Event Taxonomy
 *
 * Every event fired through `trackEvent` must follow this schema.
 * Full reference: .promobit-docs/conventions/posthog-event-taxonomy.md
 *
 * ## How to add a new event
 *
 * 1. Pick a nature: `view` (displayed to user), `click` (user action),
 *    `try` (backend call or system event).
 * 2. Name it: `{nature} {object} {detail}` in en-US, words separated by
 *    spaces. Example: `click coupon go to store`.
 * 3. Call `trackEvent` — the validator throws in dev if required props
 *    are missing or have invalid values.
 *
 * ## Quick reference
 *
 * | Nature | Required                        | Conditional            | Optional                        |
 * |--------|---------------------------------|------------------------|---------------------------------|
 * | view   | type, source_type               | source_value (†)       | —                               |
 * | click  | —                               | —                      | source_type, source_value, option |
 * | try    | source_type, status             | message (when error)   | option                          |
 *
 * (†) source_value is required on view events when type is not 'screen'.
 *     For screens, page-to-page attribution is handled by PostHog's $referrer.
 *
 * ## Examples
 *
 * @example
 * // view — screen (source_value omitted: covered by $referrer)
 * trackEvent('view coupon index page', {
 *   type: 'screen',
 *   source_type: 'navigation',
 * });
 *
 * @example
 * // view — modal (source_value required)
 * trackEvent('view coupon modal', {
 *   type: 'modal',
 *   source_type: 'interaction',
 *   source_value: 'coupon_list',
 *   coupon_id: 42,
 * });
 *
 * @example
 * // click — minimal
 * trackEvent('click coupon go to store', { coupon_id: 42 });
 *
 * @example
 * // click — with optional source
 * trackEvent('click coupon card', {
 *   id: 42,
 *   source_type: 'navigation',
 *   source_value: 'coupon_list',
 * });
 *
 * @example
 * // try — success
 * trackEvent('try login with email', { source_type: 'user', status: 'success' });
 *
 * @example
 * // try — error (message required)
 * trackEvent('try login with email', {
 *   source_type: 'user',
 *   status: 'error',
 *   message: 'invalid_credentials',
 * });
 */

export const NATURES = ['view', 'click', 'try'];

/**
 * Required properties per event nature.
 * Keys with array values validate against that set.
 * Keys with null value require presence only (free-form).
 */
export const REQUIRED_PROPERTIES = {
  view: {
    type: ['screen', 'component', 'modal', 'banner'],
    source_type: [
      'navigation',
      'scroll',
      'push',
      'deep_link',
      'system',
      'interaction',
    ],
  },
  click: {},
  try: {
    source_type: ['user', 'system'],
    status: ['success', 'error', 'pending'],
  },
};

/**
 * Optional properties per event nature.
 * Same format as REQUIRED_PROPERTIES but only validated when present.
 * Enum values are checked if the property is passed; missing is fine.
 */
export const OPTIONAL_PROPERTIES = {
  view: {},
  click: {
    source_type: ['navigation', 'interaction'],
    source_value: null,
    option: null,
  },
  try: {
    option: null,
  },
};

/**
 * Properties that are conditionally required.
 * Format: { nature: { property: { when: { field, value } } } }
 * Use `notValue` instead of `value` to require when field !== notValue.
 */
export const CONDITIONAL_PROPERTIES = {
  view: {
    source_value: { when: { field: 'type', notValue: 'screen' } },
  },
  try: {
    message: { when: { field: 'status', value: 'error' } },
  },
};
